import { Component, OnInit } from '@angular/core';
import { LayoutService } from '@common/services/layout.service';
import { ModalService } from '@common/services/modal.service';
import { SortableExtractor } from '@common/UI/sortable/sortable.component';
import { Filterable } from '@common/utilities/Filterable';
import { commit, download, main } from '@core/operators';
import { ApiService } from '@core/services/api.service';
import { ConfigsService } from '@core/services/configs.service';
import { CurrenciesService } from '@core/services/currencies.service';
import { calculateExportPrice, DataService, GoodRecord, InvoiceCategory, InvoiceState, InvoiceType, SKU2Parts, ProductUnit } from '@core/services/data.service';
import { PartnersService } from '@core/services/partners.service';
import { ProductsService } from '@core/services/products.service';
import { SynchronizationService } from '@core/services/synchronization.service';
import { Multilingual } from '@core/utilities/multilingual';
import { MessageBoxService } from '@routes/common/message-box.service';
import { GoodEditorComponent } from '@routes/editors/good-editor/good-editor.component';
import { PartnerSelectorComponent } from '@routes/editors/partner-selector/partner-selector.component';
import { ViewerComponent } from '@routes/invoices/viewer/viewer.component';
import { map } from 'rxjs/operators';
import { TagsService } from '@core/services/tags.service';
import { XLSX } from '@core/utilities/xlsx';
import { DatePipe } from '@angular/common';

interface Group {

  name: string;

  SKUs: string[];
  Colors: string[];

  LengthCount: number;
  WeightCount: number;
  LengthQuantity: number;
  WeightQuantity: number;

  ExportingLengthCount: number;
  ExportingWeightCount: number;
  ExportingLengthQuantity: number;
  ExportingWeightQuantity: number;

  children: Group[];
  goods: GoodRecord[]
}

function sku(sku: string): string {
  let parts = SKU2Parts(sku);
  return parts[0].length > 1 ? parts[0] + '-' + parts[1] : parts[0] + parts[1];
 }

@Component({  
  templateUrl: './goods.component.html',
  styleUrls: ['./goods.component.scss']
})
export class GoodsComponent implements OnInit {

  public groups = new Filterable<Group>({
    extractor: i => [i.name],
    grouper: i => [
      { name: 'Loại hàng', option: i.LengthCount > 0 ? (i.WeightCount > 0 ? 'Hàng hỗn hợp' : 'Hàng mét') : 'Hàng cân' },
      { name: 'Trạng thái xuất', option: i.ExportingLengthCount + i.ExportingWeightCount > 0 ? 'Có chờ xuất' : 'Không chờ xuất' }
    ]
  });

  public goods = new Filterable<GoodRecord>({
    extractor: i => [i.ActualQuantity + (i.Unit == 1 ? 'm' : 'kg')],
    grouper: i => [{ name: 'Loại hàng', option: i.Unit == 1 ? 'Hàng mét' : 'Hàng cân' }]
  })


  public group: Group = null;

  public paths: Group[] = [];

  public sum(field: string, source: any[], unit?: ProductUnit) {
    switch (field) {
      case 'Count': return source.sum(i => i.WeightCount + i.LengthCount);
      case 'ExportingCount': return source.sum(i => i.ExportingLengthCount + i.ExportingWeightCount);
      case 'RemainingCount': return source.sum(i => i.WeightCount + i.LengthCount - i.ExportingLengthCount - i.ExportingWeightCount);
      case 'RemainingLengthQuantity': return source.sum(i => i.LengthQuantity - i.ExportingLengthQuantity);
      case 'RemainingWeightQuantity': return source.sum(i => i.WeightQuantity - i.ExportingWeightQuantity);
      case 'ReducedQuantity': return source.sum(i => !unit || unit == i.Unit ? i.ActualQuantity - i.ImportQuoteQuantity : 0);
      default: return source.sum(i => !unit || unit == i.Unit ? i[field] : 0);
    }
  }  

  constructor(
    private ds: DataService,
    private modal: ModalService,
    private mb: MessageBoxService,
    private layout: LayoutService,
    private sync: SynchronizationService,
    private partners: PartnersService,
    public configs: ConfigsService,
    private products: ProductsService,
    private currencies: CurrenciesService,
    private tags: TagsService,
    private date: DatePipe
  ) { }

  public get loading(): boolean {
    return !this.sync.ready(PartnersService, GoodsComponent, ConfigsService, ProductsService, CurrenciesService);
  } 

  public exportExcel() {
    if (!this.groups.empty) {
      this.exportExcelOfGroup();
    }
    else {
      this.exportExcelOfGoods();
    }
  }

  private exportExcelOfGroup() {
    
    this.sync.when(GoodsComponent).commit(() => {

      let xlsx = new XLSX();
      let sheet = xlsx.worksheet(this.paths.map(i => i.name).join(' - '));

      xlsx.decimalFormat = '##,0.0';

      let rows: any[][] = [];

      rows.push(['Mã hàng', 'Màu', 'Tổng có (cây)', 'Tổng có (m)', 'Chờ xuất (cây)', 'Chờ xuất (m)', 'Còn lại (cây)', 'Còn lại (m)'].map(i => i.toUpperCase()));

      ['left', 'center', 'right', 'right', 'right', 'right', 'right'].forEach((align, i) => sheet.column(i + 1).align = align as any);
      
      this.groups.source.forEach(group => {
        rows.push([group.SKUs[0], 
          group.children.length < 1 ? group.Colors[0] : group.Colors.length, 
          group.LengthCount || group.WeightCount, 
          group.LengthQuantity || group.WeightQuantity, 
          group.ExportingLengthCount || group.ExportingWeightCount, 
          group.ExportingLengthQuantity || group.ExportingWeightQuantity, 
          (group.LengthCount || group.WeightCount) - (group.ExportingLengthCount || group.ExportingWeightCount),
          (group.LengthQuantity || group.WeightQuantity) - (group.ExportingLengthQuantity || group.ExportingWeightQuantity)
        ]);
      })

      rows.push(['TỔNG', '-', 
        this.groups.source.sum( i => i.LengthCount || i.WeightCount), 
        this.groups.source.sum( i => i.LengthQuantity || i.WeightQuantity), 
        this.groups.source.sum(i => i.ExportingLengthCount || i.ExportingWeightCount), 
        this.groups.source.sum(i => i.ExportingLengthQuantity || i.ExportingLengthQuantity), 
        this.groups.source.sum(i => (i.LengthCount || i.WeightCount) - (i.ExportingLengthCount || i.ExportingWeightCount)),
        this.groups.source.sum(i => (i.LengthQuantity || i.WeightQuantity) - (i.ExportingLengthQuantity || i.ExportingWeightQuantity))
      ]);      
        
      sheet.frozenTopRowCount = 1;

      rows.forEach((row, i) => sheet.row(i + 1, ...row));

      xlsx.generate().subscribe(base64 => download(this.paths.map(i => i.name).join(' - ') + " (" + this.date.transform(new Date(), 'dd-MM-yyyy HH:mm:ss z') + ").xlsx", base64));
    });    
  }

  private exportExcelOfGoods() {
    this.sync.when(GoodsComponent).commit(() => {

      let xlsx = new XLSX();
      let sheet = xlsx.worksheet(this.paths.map(i => i.name).join(' - '));

      xlsx.decimalFormat = '##,0.0';

      let rows: any[][] = [];

      rows.push(['Nhập từ', 'Xuất tới', 'Trạng thái', 'Loại', 'Dài gốc', 'Cắt', 'Dài thực', 'Khấu trừ', 'Dài tính giá'].map(i => i.toUpperCase()));

      ['center', 'center', 'center', 'center', 'right', 'right', 'right', 'right', 'right'].forEach((align, i) => sheet.column(i + 1).align = align as any);
      
      this.goods.source.forEach(good => {
        rows.push([
          this.partner(good.VendorID),
          good.CustomerID > 0 ? this.partner(good.CustomerID) : '-',
          this.state(good),
          this.type(good),
          good.ActualQuantity + this.splitted(good),
          this.splitted(good),
          good.ActualQuantity,
          good.ActualQuantity - good.ImportQuoteQuantity,
          good.ImportQuoteQuantity
        ]);
      })

      rows.push(['TỔNG', '-', '-', '-',
        this.goods.source.sum( i => i.ActualQuantity + this.splitted(i)), 
        this.goods.source.sum( i => this.splitted(i)),
        this.goods.source.sum(i => i.ActualQuantity),
        this.goods.source.sum(i => i.ActualQuantity - i.ImportQuoteQuantity),
        this.goods.source.sum(i => i.ImportQuoteQuantity)        
      ]);      
        
      sheet.frozenTopRowCount = 1;

      rows.forEach((row, i) => sheet.row(i + 1, ...row));

      xlsx.generate().subscribe(base64 => download(this.paths.map(i => i.name).join(' - ') + " (" + this.date.transform(new Date(), 'dd-MM-yyyy HH:mm:ss z') + ").xlsx", base64));
    });
  }


  public partner(id: number): string {

    let partner = this.partners.get(id);
    return partner ? partner.Name : Multilingual.getText('CHƯA CÓ');
  }

  public type(good: GoodRecord): string {
    return good.ParentID > 0 ? 'cắt' : 'gốc';
  }

  public splitted(good: GoodRecord): number {
    return good.Children.sum(i => i.ActualQuantity);
  }

  public state(good: GoodRecord) {
    return good.ExportInvoiceID > 0 ? 'chờ xuất' : 'tồn kho';
  }

  public groupExtractor(field: string): SortableExtractor<Group> {
    return {
      extract: i => {
        switch (field) {
          case 'SKU': return i.SKUs[0];
          case 'Colors': return i.Colors.length;
          case 'Count': return i.LengthCount + i.WeightQuantity;
          case 'Quantity': return i.LengthQuantity + i.WeightQuantity;
          case 'ExportingCount': return i.ExportingLengthCount + i.ExportingWeightCount;
          case 'ExportingQuantity': return i.ExportingLengthQuantity + i.ExportingWeightQuantity;
          case 'RemainingCount': return i.LengthCount + i.WeightCount - i.ExportingLengthCount - i.ExportingWeightCount;
          case 'RemainingQuantity': return i.LengthQuantity + i.WeightQuantity - i.ExportingLengthQuantity - i.ExportingWeightQuantity;
          default: return i[field]
        }
      }
    }
  }

  public goodExtractor(field: string): SortableExtractor<GoodRecord> {
    return {
      extract: i => {
        switch (field) {
          case 'ImportInvoice': return i.ImportInvoiceTag + '.' + i.ImportStamp;
          case 'ReducedQuantity': return i.ActualQuantity - i.ImportQuoteQuantity;
          case 'OriginalQuantity': return i.ActualQuantity + this.splitted(i);
          case 'SplittedQuantity': return this.splitted(i);
          case 'Type': return this.type(i);
          case 'Vendor': return this.partner(i.VendorID).toUpperCase();
          case 'Customer': return this.partner(i.CustomerID).toUpperCase();
          case 'State': return Multilingual.getText(this.state(i));
          default: return i[field]
        }
      }
    }
  }

  public synchronize() {

    let goods: GoodRecord[] = [];

    this.sync.with(GoodsComponent).repeat(
      () => this.ds.goods('remaining').pipe(map(data => data ? commit<boolean>(() => goods = data).return(true) : false))
    );

    this.sync.when(PartnersService, TagsService, GoodsComponent, ConfigsService, ProductsService, CurrenciesService).commit(() => {

      let root: Group = {

        name: Multilingual.getText('Hàng tồn kho'),

        SKUs: goods.map(i => i.SKU).distinct(),
        Colors: goods.map(i => i.Color).distinct(),

        LengthQuantity: goods.sum(i => i.Unit == ProductUnit.Meter ? i.ActualQuantity : 0),
        WeightQuantity: goods.sum(i => i.Unit == ProductUnit.Kilogram ? i.ActualQuantity : 0),
        LengthCount: goods.count(i => i.Unit == ProductUnit.Meter),
        WeightCount: goods.count(i => i.Unit == ProductUnit.Kilogram),

        ExportingLengthQuantity: goods.sum(i => i.Unit == ProductUnit.Meter && i.ExportInvoiceID > 0 ? i.ActualQuantity : 0),
        ExportingWeightQuantity: goods.sum(i => i.Unit == ProductUnit.Kilogram && i.ExportInvoiceID > 0 ? i.ActualQuantity : 0),
        ExportingLengthCount: goods.count(i => i.Unit == ProductUnit.Meter && i.ExportInvoiceID > 0),
        ExportingWeightCount: goods.count(i => i.Unit == ProductUnit.Kilogram && i.ExportInvoiceID > 0),

        goods: goods,

        children: goods.group(i => i.SKU).map<Group>(i => ({

          name: i.key as string,

          SKUs: [i.key as string],
          Colors: i.items.map(i => i.Color).distinct(),

          LengthQuantity: i.items.sum(i => i.Unit == ProductUnit.Meter ? i.ActualQuantity : 0),
          WeightQuantity: i.items.sum(i => i.Unit == ProductUnit.Kilogram ? i.ActualQuantity : 0),
          LengthCount: i.items.count(i => i.Unit == ProductUnit.Meter),
          WeightCount: i.items.count(i => i.Unit == ProductUnit.Kilogram),

          ExportingLengthQuantity: i.items.sum(i => i.Unit == ProductUnit.Meter && i.ExportInvoiceID > 0 ? i.ActualQuantity : 0),
          ExportingWeightQuantity: i.items.sum(i => i.Unit == ProductUnit.Kilogram && i.ExportInvoiceID > 0 ? i.ActualQuantity : 0),
          ExportingLengthCount: i.items.count(i => i.Unit == ProductUnit.Meter && i.ExportInvoiceID > 0),
          ExportingWeightCount: i.items.count(i => i.Unit == ProductUnit.Kilogram && i.ExportInvoiceID > 0),

          goods: i.items,

          children: i.items.group(x => x.Color).map<Group>(i => ({
            name: Multilingual.getText('màu') + ' ' + i.key,

            SKUs: [i.items[0].SKU],
            Colors: [i.key as string],

            LengthQuantity: i.items.sum(i => i.Unit == ProductUnit.Meter ? i.ActualQuantity : 0),
            WeightQuantity: i.items.sum(i => i.Unit == ProductUnit.Kilogram ? i.ActualQuantity : 0),
            LengthCount: i.items.count(i => i.Unit == ProductUnit.Meter),
            WeightCount: i.items.count(i => i.Unit == ProductUnit.Kilogram),

            ExportingLengthQuantity: i.items.sum(i => i.Unit == ProductUnit.Meter && i.ExportInvoiceID > 0 ? i.ActualQuantity : 0),
            ExportingWeightQuantity: i.items.sum(i => i.Unit == ProductUnit.Kilogram && i.ExportInvoiceID > 0 ? i.ActualQuantity : 0),
            ExportingLengthCount: i.items.count(i => i.Unit == ProductUnit.Meter && i.ExportInvoiceID > 0),
            ExportingWeightCount: i.items.count(i => i.Unit == ProductUnit.Kilogram && i.ExportInvoiceID > 0),

            children: [],
            goods: i.items
          }))
        }))        
      }      

      this.open(root);

      if (this.groups.empty) this.mb.informs("Hiện tại không có hàng tồn kho. Xin vui lòng quay lại sau!");
    });
  }

  ngOnInit(): void {

    main(() => {
      this.layout.title = Multilingual.getText("Hàng tồn kho")
    });

    this.synchronize();
  }

  public open(group: Group) {

    if (group != this.group) {

      if (!this.paths.contains(group)) this.paths.push(group);
      this.paths = this.paths.take(this.paths.indexOf(group) + 1);
      this.group = group;

      if (group.children.length == 1) {
        this.open(group.children[0]);
        return;
      }
      
      this.groups.source = group.children;
      this.goods.source = group.goods;
    }
  }

  
  public split(i: GoodRecord) {
    this.modal.show(GoodEditorComponent, { state: { good: i } }).content.onSplitted.subscribe(good => {
      this.group.goods = this.goods.source = this.group.goods.union([good]);
      this.group.LengthCount++;
    })
  }  
}
